<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import { useFormatTime } from '@/Composables/useFormatTime'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import Button from '@/Components/Utils/Button.vue'
import Icon from '@/Components/Utils/Icon.vue'
import Tag from '@/Components/Utils/Tag.vue'
import CountUp from 'vue-countup-v3'
import { computed, inject } from 'vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'

interface Stat {
    label: string
    value: number
    unit?: string | null
    type?: string
}

const props = defineProps<{
    pageHead: PageHeadingTS
    title?: string
    data: {
        name: string
        slug: string
        description?: string
        created_at?: string
        is_inactive?: boolean
        sales_name?: string
    }
    owner?: {
        name: string
        slug: string
        city?: string
        is_inactive?: boolean
        brands_count: number
    } | null
    stats: {
        order: Stat
        quantity: Stat
        revenue: Stat
        unpaid: Stat
    }
    type_breakdown: {
        label: string
        icon: { icon: string, class?: string, tooltip?: string }
        value: number
        quantity: number
    }[]
    canceled_count: number
    last_order_at?: string | null
    recent_orders: any[]
}>()

const layout = inject('layout', layoutStructure)

const linkAllOrders = computed(() => route('sales.orders.index', { 'filter[global]': props.data.name }))

const statTiles = computed<Stat[]>(() => [
    props.stats.order,
    props.stats.quantity,
    props.stats.revenue,
    props.stats.unpaid,
])

const formatStat = (stat: Stat) => stat.type === 'currency' ? useFormatCurrency(stat.value) : useFormatNumber(stat.value)
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <!-- Button: delete -->
        <template #button-delete-data="{ action }">
            <div>
                <ModalConfirmationDelete
                    :routeDelete="action.route"
                    isFullLoading
                >
                    <template #default="{ isOpenModal, changeModel }">
                        <Button
                            @click="() => changeModel()"
                            :style="action.style"
                            :label="action.label"
                            :icon="action.icon"
                            :iconRight="action.iconRight"
                            :key="`ActionButton${action.label}${action.style}`"
                            :tooltip="action.tooltip"
                        />
                    </template>
                </ModalConfirmationDelete>
            </div>
        </template>
    </PageHeading>

    <div class="px-4 mt-4 grid gap-4 md:grid-cols-2">
        <!-- Detail brand -->
        <div class="h-fit w-full overflow-hidden rounded-xl border border-sky-400">
            <div class="flex flex-col gap-y-1 border-b border-gray-900/5 bg-gray-50 p-6">
                <div class="flex items-center gap-x-2 font-bold">
                    {{ data.name }}
                    <Tag v-if="data.is_inactive" label="Inactive" :theme="7" noHoverColor />
                </div>
                <div class="text-sm/6 text-gray-500">{{ data.description || 'Tanpa deskripsi' }}</div>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Owner</dt>
                    <dd class="text-right font-medium">
                        <Link v-if="owner" :href="route('sales.customers.show', owner.slug)" class="secondaryLink">
                            {{ owner.name }}
                        </Link>
                        <span v-else class="italic text-gray-400">Customer tidak ditemukan</span>
                        <div v-if="owner?.city" class="font-normal text-gray-400">{{ owner.city }}</div>
                    </dd>
                </div>

                <div v-if="owner" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Brand lain milik owner</dt>
                    <dd class="font-medium">{{ useFormatNumber(owner.brands_count - 1) }}</dd>
                </div>

                <div v-if="layout?.auth?.role === 'sales_special'" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Sales</dt>
                    <dd class="font-medium">{{ data.sales_name || '-' }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Tgl. dibuat</dt>
                    <dd class="font-medium">{{ useFormatTime(data.created_at) }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Order terakhir</dt>
                    <dd class="font-medium">
                        {{ last_order_at ? useFormatTime(last_order_at) : '-' }}
                    </dd>
                </div>

                <div v-if="canceled_count > 0" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Order dibatalkan</dt>
                    <dd class="font-medium text-red-500">{{ useFormatNumber(canceled_count) }}</dd>
                </div>
            </dl>
        </div>

        <!-- Stats -->
        <div class="h-fit grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div
                v-for="stat in statTiles"
                :key="stat.label"
                class="relative rounded border border-gray-200 px-4 py-6 sm:px-6"
            >
                <p class="text-sm/6 font-medium text-gray-400">{{ stat.label }}</p>
                <p class="mt-2 flex flex-wrap items-baseline gap-x-2">
                    <span class="text-3xl font-semibold tracking-tight tabular-nums">
                        <CountUp
                            :endVal="stat.value"
                            :duration="1.5"
                            :scrollSpyOnce="true"
                            :options="{ formattingFn: () => formatStat(stat) }"
                        />
                    </span>
                    <span v-if="stat.unit" class="text-sm" :class="stat.label === 'Belum lunas' ? 'text-red-500' : 'text-green-500'">
                        {{ stat.unit }}
                    </span>
                </p>
            </div>
        </div>
    </div>

    <!-- Rincian per tipe produk -->
    <div class="px-4 mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div
            v-for="type in type_breakdown"
            :key="type.label"
            class="rounded border border-gray-200 px-4 py-4"
        >
            <p class="flex items-center gap-x-2 text-sm/6 font-medium text-gray-400">
                <Icon :data="type.icon" />
                {{ type.label }}
            </p>
            <p class="mt-1 flex items-baseline gap-x-2">
                <span class="text-2xl font-semibold tabular-nums">{{ useFormatNumber(type.value) }}</span>
                <span class="text-sm text-gray-400">order &middot; {{ useFormatNumber(type.quantity) }} pcs</span>
            </p>
        </div>
    </div>

    <!-- Order terbaru -->
    <div class="px-4 mt-6 mb-8">
        <div class="flex items-baseline justify-between gap-x-4">
            <h2 class="font-semibold">Order terbaru</h2>
            <Link v-if="recent_orders.length" :href="linkAllOrders" class="secondaryLink text-sm">
                Lihat semua order
            </Link>
        </div>

        <div v-if="recent_orders.length" class="mt-2 overflow-x-auto rounded-xl border border-gray-200">
            <table class="min-w-full divide-y divide-gray-200 text-sm">
                <thead class="bg-gray-50 text-left text-gray-500">
                    <tr>
                        <th class="px-4 py-3 font-medium">Kode</th>
                        <th class="px-4 py-3 font-medium">Tgl. dibuat</th>
                        <th class="px-4 py-3 font-medium">Produk</th>
                        <th class="px-4 py-3 font-medium text-right">Kuantitas</th>
                        <th class="px-4 py-3 font-medium">Manufaktur</th>
                        <th class="px-4 py-3 font-medium">Pembayaran</th>
                        <th class="px-4 py-3 font-medium text-right">Total</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr
                        v-for="order in recent_orders"
                        :key="order.id"
                        :class="order.canceled_at ? 'bg-red-50 text-gray-400' : ''"
                    >
                        <td class="px-4 py-3">
                            <Link :href="route('sales.orders.show', order.slug)" class="primaryLink">
                                {{ order.reference }}
                            </Link>
                            <div v-if="order.canceled_at" class="text-xs text-red-500">Dibatalkan</div>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap">{{ useFormatTime(order.created_at) }}</td>
                        <td class="px-4 py-3">
                            <div class="flex items-center gap-x-1">
                                <Icon :data="order.type_icon" />
                                <span v-if="order.product_name">{{ order.product_name }}</span>
                                <span v-else class="italic text-gray-400">Produk tidak ditemukan</span>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-right tabular-nums">{{ useFormatNumber(order.quantity) }}</td>
                        <td class="px-4 py-3">
                            <span v-if="order.manufacture?.state_label" :class="order.manufacture?.state_icon?.class">
                                {{ order.manufacture.state_label }}
                            </span>
                            <span v-else class="text-gray-400">-</span>
                        </td>
                        <td class="px-4 py-3">
                            <Tag
                                :label="order.billing_status_label"
                                :theme="order.billing_status === 'paid' ? 3 : 2"
                                noHoverColor
                            />
                        </td>
                        <td class="px-4 py-3 text-right tabular-nums">{{ useFormatCurrency(order.final_total) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-else class="mt-2 rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center text-gray-400">
            Brand ini belum punya order.
        </div>
    </div>
</template>
